import { h, Component } from 'preact';
import Store from 'store/store';
import { joinClasses } from 'utils/utils';
import { getAllMovies, getAllSections } from 'utils/api';
import { UNIVERSAL_STUDIO_SLUG, FOCUS_STUDIO_SLUG } from 'utils/constants';
import { getPublishedMovies } from 'utils/data';
import { showLogin, getCurrentUser, getRegions, hideLogin } from 'utils/login';
import { getAllMovieStatuses } from 'utils/movieStatus';
import { alert, LeaveConfirmationModal, LeaveWithErrorsModal, showModal } from 'utils/modal';
import { hasUnsavedChanges, revertData, save, scheduleMoviesRefresh, scheduleSectionsRefresh, stopMovieRefresh, stopSectionsRefresh } from 'utils/data';
import { hasErrors } from 'utils/errors';
import { getCurrentDataPath } from 'utils/editor';
import { replaceWithVNode } from 'utils/vnode';
import { canUser } from 'utils/roles';
import { isBaseRegion } from 'utils/studioConfig';

import ContactLink from 'components/contactLink/contactLink';
import Login from 'components/login/login';
import Main from 'components/main/main';
import Editor from 'components/editor/editor';
import LoadingBar from 'components/loadingBar/loadingBar';

import s from 'base/universalCMS/universalCMSApp.sss';

const indexed = (arr, map = e => e) => Object.fromEntries(arr.map(e => [e.id, map(e)]));

const listenForFunctionCall = (obj, name, listener) => {
	const original = obj[name];
	obj[name] = function() {
		original.apply(this, arguments);
		listener.apply(this, arguments);
	};
	return { revert: () => obj[name] = original };
};

export default class UniversalCMSApp extends Component {
	constructor(props) {
		super(props);

		this.state = {
			ready: false,
			store: props.initialStore,
		};

		this.reset = this.reset.bind(this);
		this.loadContent = this.loadContent.bind(this);
		this.loadPublishData = this.loadPublishData.bind(this);
		this.setStateFromURL = this.setStateFromURL.bind(this);
		this.onMessage = this.onMessage.bind(this);
		this.onClick = this.onClick.bind(this);
		this.confirmLeaving = this.confirmLeaving.bind(this);

		Store.set(props.initialStore);
		Store.on('update', () => this.setState({ store: Store.get() }));

		// Here we would typically check if window !== undefined first, but we don't currently do server-side rendering of the CMS
		this.setStateFromURL();
	}

	componentDidMount() {
		// TODO (when it becomes more available): navigation API (https://developer.mozilla.org/en-US/docs/Web/API/Navigation_API)
		window.addEventListener('popstate', this.setStateFromURL);
		window.addEventListener('hashchange', this.setStateFromURL);
		window.addEventListener('message', this.onMessage);
		window.addEventListener('beforeunload', this.confirmLeaving);

		Store.on('RESET', this.reset);
		Store.on('RELOAD', this.loadContent);
		Store.on('PUBLISH_SUCCESS', this.loadPublishData);

		if (window.name === 'preview') {
			// Avoid previews opening in the same tab if we somehow load the main CMS in a preview tab
			window.name = '';
		}

		this._historyPushState = listenForFunctionCall(history, 'pushState', this.setStateFromURL);
		this._historyReplaceState = listenForFunctionCall(history, 'replaceState', this.setStateFromURL);

		this.init();
	}

	componentWillUnmount() {
		stopMovieRefresh();
		stopSectionsRefresh();

		window.removeEventListener('popstate', this.setStateFromURL);
		window.removeEventListener('hashchange', this.setStateFromURL);
		window.removeEventListener('message', this.onMessage);
		window.removeEventListener('beforeunload', this.confirmLeaving);

		Store.off('RESET', this.reset);
		Store.off('RELOAD', this.loadContent);
		Store.off('PUBLISH_SUCCESS', this.loadPublishData);

		this._historyPushState?.revert();
		this._historyReplaceState?.revert();
	}

	setActiveUser(user) {
		Store.get().set({ user }).now();
		this.loadContent();
	}

	async init() {
		const user = await getCurrentUser();

		if (!user) {
			await showLogin();
			return;
		}

		this.setActiveUser(user);
	}

	reset() {
		this.setState({ ready: false });
		Store.get().set({ data: null, savedData: null });
	}

	async loadRegions() {
		const { domains, user } = Store.get();
		if (!user) throw new Error('Cannot load data: no active user');

		// Load regions if not already done
		if (!domains) {
			let regions = user.regions;
			if (regions) {
				regions = Object.entries(regions).map(([id, region]) => ({
					region_id: id,
					...region,
				}));
			} else {
				regions = await getRegions();
			}
			const selectedRegion = user.region_id;
			const selectedRegionObj = regions.find(r => r.region_id === selectedRegion);

			if (!selectedRegion || !regions?.length || !selectedRegionObj) {
				throw new Error('Cannot load data: user has no regions assigned');
			}

			const studioSlug = selectedRegionObj.studio_slug;
			const regionCode = selectedRegionObj.region_code;
			const isUniversal = studioSlug === UNIVERSAL_STUDIO_SLUG;
			const isFocus = studioSlug === FOCUS_STUDIO_SLUG;

			Store.get().set({ domains: { regions, selectedRegion, studioSlug, regionCode, isUniversal, isFocus } }).now();
		}
	}

	async loadContent() {
		if (this.loadingContent) return;
		this.loadingContent = true;
		this.setState({ dataError: false });
		try {
			await this.loadRegions();

			// NOTE: we will probably need to update that once the data amount grows - each page should load what it needs
			const [movies, sections] = await Promise.all([
				getAllMovies(),
				getAllSections(),
			]);
			const pages = sections.filter(section => section.section_type === 'page');
			const navigation = sections.find(section => section.section_type === 'navigation');
			const movieStatuses = await getAllMovieStatuses(movies);
			const movieLocales = isBaseRegion() ? indexed(movies, movie => ({ ...movie.locales, base_movie_id: movie.id })) : null;
			const publishedDataPromise = this.loadPublishData();

			const data = { movies: indexed(movies), pages: indexed(pages), navigation };
			const now = Date.now();
			Store.get().set({
				baseMovies: null, // Ensure it is cleared for changing regions - will be loaded on demand when needed
				data,
				savedData: { ...data, lastMoviesUpdate: now, lastSectionsUpdate: now },
				movieStatuses,
				movieLocales,
				publishedDataPromise,
			}).now();


			this.setState({ ready: true, dataError: false });

			// Re-evaluate path now that regions are loaded (handles redirect for base regions)
			this.setStateFromURL();

			// Start a periodic refresh of the data (should these only be done when the relevant data might be used ? Might be tricky to know for sure)
			scheduleMoviesRefresh();
			scheduleSectionsRefresh();
		} catch (e) {
			console.error(e);
			this.setState({ dataError: { error: e, at: new Date() } });
		}
		this.loadingContent = false;
	}

	async loadPublishData() {
		try {
			Store.get().set({ publishedData: {} }).now();
			if (isBaseRegion()) return;
			const publishedMovies = await getPublishedMovies();
			Store.get().set({ publishedData: { movies: publishedMovies } }).now();
		} catch (e) {
			console.error(e);
			this.setState({ dataError: { error: e, at: new Date() } });
		}
	}

	setStateFromURL() {
		let path = location.hash.slice(1).split('/');
		const isBase = isBaseRegion();

		if (path.length === 1 && path[0] === '') {
			path = ['movies'];
		}

		// Redirect to movies if trying to access pages/navigation in base region
		if (isBase && (path[0] === 'pages' || path[0] === 'navigation')) {
			path = ['movies'];
			history.replaceState(null, '', '#movies');
		}

		let store = Store.get();
		if (path.join('/') !== store.path.join('/')) {
			store = store.set({ path });
		}
	}

	onMessage(e) {
		// Ensure message integrity? Requests will be blocked by backend anyway if they bypass the auth
		// if (e.origin !== API_ORIGIN) {
		// 	return;
		// }
		if (e.data?.type === 'auth-success') {
			hideLogin();
			// this.setState({ ready: true });
			return this.setActiveUser(e.data.user);
		}
	}

	async onClick(e) {
		const link = e.target?.closest(':link');
		if (!link) return;
		const target = link.target || '_self';
		if (target !== '_self') return;
		const isExternal = link.origin !== location.origin;

		const confirmationMessage = this.confirmLeaving();
		const hasConfirmation = confirmationMessage && typeof confirmationMessage === 'string';

		if (isExternal && !hasConfirmation) return;

		e.preventDefault();

		let href = link.href || '';
		if (href.endsWith('#')) href = href.slice(0, -1);

		if (location.href === href) return;

		const path = getCurrentDataPath();
		// path?.startsWith('/movies/')
		const dataHasErrors = canUser('write') && hasErrors(path);

		if (hasConfirmation || dataHasErrors) {
			// const ok = await confirm(confirmationMessage, { title: 'Save before exiting?', button: 'Discard and continue', cancel: 'Stay on this page' });
			// const confirmationMessageFormatted = confirmationMessage.split('\n').reduce((a, e, i) => a.concat(i ? [<br />, e] : e), []);
			const modal = dataHasErrors ? showModal(LeaveWithErrorsModal) : showModal(LeaveConfirmationModal, { content: replaceWithVNode(confirmationMessage, '\n', <br />) });
			const confirmationResult = await modal;
			if (confirmationResult === 'CANCEL') return;
			if (confirmationResult === 'SAVE' && path) {
				try {
					await save(path);
				} catch (e) {
					console.error('Error saving data before leaving:', e);
					alert('Sorry, an error occurred while saving', { title: 'Error' });
					return;
				}
			}
			if (isExternal) return location.href = link.href;
			if (confirmationResult !== 'SAVE' && path) {
				revertData(path);
			}
		}

		history.pushState(null, '', href);
	}

	confirmLeaving(e) {
		if (!canUser('write')) return;
		const path = getCurrentDataPath();
		if (!path) return false;
		let changesCount = hasUnsavedChanges(path);
		if (!changesCount) return;
		// let message = `You have ${changesCount} unsaved changes.\nWhat would you like to do?`;
		let message = `You have some unsaved changes. What would you like to do?`;
		if (e) {
			e.preventDefault();
			e.returnValue = message;
		}
		return message;
	}

	renderContent() {
		const dataError = this.state.dataError;
		if (dataError) {
			return (
				<div class={s.error}>
					<h2>Error</h2>
					<p>Sorry, something went wrong while loading this page</p>
					<button onClick={this.loadContent}>Try again</button>
					<span> or </span>
					<ContactLink
						subject="Universal CMS loading issue"
						body={[`The site did not load properly at ${dataError.at.toISOString()}\n`, `Browser: ${navigator.userAgent}\n`, 'Error details:', dataError.error.message, dataError.error.stack].filter(e => e).join('\n') + '\n'}
					>
						Contact us
					</ContactLink>
				</div>
			);
		}
		if (!this.state.ready) {
			return <LoadingBar />;
		}
		const store = this.state.store;
		let ActiveComponent = Main;
		// TODO: improve how we do this probably
		if (store.path.length > 1 && (store.path[0] === 'pages' || store.path[0] === 'movies')) {
			ActiveComponent = Editor;
		}
		return <ActiveComponent loading={this.loadingContent} user={store.user} domains={store.domains} path={store.path} data={store.data} savedData={store.savedData} dataPath={getCurrentDataPath()} />;
	}

	render(props, state) {
		return (
			<div class={joinClasses(s.container)} id="universalCMS" onClickCapture={this.onClick}>
				<Login />
				{this.renderContent()}
			</div>
		);
	}
}
